import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EntrustPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Entrust" — an EXPIRATION / win-back paywall built on TRUST + BELONGING, told
// through the one security gesture no other paywall uses: re-pairing a DEVICE
// into your vault's trusted circle (a re-pair HANDSHAKE, not a lock/seal/dial).
//
// Premium lapsed, so the private, end-to-end encrypted SYNC between your devices
// went on standby — your memories never moved and are still yours on this phone,
// but your other device drifted out of the trusted circle and the full watch is
// paused. You DRAG your device home to its dock on the ring; as it nears, the
// dormant tether tautens, a handshake ripple fires, the encrypted bond SEALS,
// your memories mirror across the link, and all four real protections arm in one
// beat — Only-you unlock · Encrypted backup · Break-in alerts · Private sync.
//
// The control IS the hero and it is the app's real trusted-device / private-sync
// affordance. Register: warm, connected reassurance — "bring it all back into
// your circle" — not fear or countdowns. Diverges from every taken mechanic
// (trace, wax seal, slider, plug, switch, deadbolt, dial, ECG…) and lights the
// protections TOGETHER, not as a sequential checklist.
//
// Honest & App-Store compliant: the featured, most-prominent price is the yearly
// plan (7-day free trial, per-month broken out, real Save %); monthly is a small
// secondary line. No countdown, no scarcity, no data-loss claim — the link is
// dormant, never broken; your photos are never deleted or locked away from you.
// Reuses only the house font, colour tokens and bundled memory photos. Renders
// on debug data, no native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// Memories that mirror onto your device across the link on pairing.
const MEMORIES = ["m04", "m10", "m01", "m08"].map(photo)
// A tighter, vivid stack of memories inside your vault core (top = last).
const CORE_MEMORIES = ["m04", "m08", "m01"].map(photo)

// Geometry in a 0-100 square. Core sits high-centre; the trusted ring rings it;
// four protections dock at the ring's cardinals-of-the-diamond; your device docks
// at the bottom slot; it starts drifted off to the lower-right.
const CORE = { x: 50, y: 48 }
const RING_R = 32
const DOCK = { x: 50, y: CORE.y + RING_R } // (50, 80) — your device's home slot
const START = { x: 87, y: 93 } // drifted out of the circle
const SNAP_R = 8 // pair when the device gets this close to its dock

const onRing = (deg: number) => ({
  x: CORE.x + RING_R * Math.cos((deg * Math.PI) / 180),
  y: CORE.y + RING_R * Math.sin((deg * Math.PI) / 180),
})

// Four REAL Premium protections, seated on the trusted ring. They arm together
// the instant the circle closes — presented as points ON the instrument, not a
// bullet list.
const GUARDS: { label: string; deg: number; icon: JSX.Element }[] = [
  {
    label: "Only-you unlock",
    deg: 225,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M7 10V7.4a5 5 0 0 1 10 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="4.8"
          y="10"
          width="14.4"
          height="10"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.6" r="1.5" fill="var(--en-core-hole)" />
      </svg>
    ),
  },
  {
    label: "Encrypted backup",
    deg: 315,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.7 12.2l2.2 2.2 4.4-4.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Break-in alerts",
    deg: 180,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M2.5 12S6 5.6 12 5.6 21.5 12 21.5 12 18 18.4 12 18.4 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },
  {
    label: "Private sync",
    deg: 0,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M5 13a7 7 0 0 1 12.3-4.5M19 11a7 7 0 0 1-12.3 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M17 4.5V8h-3.5M7 19.5V16h3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

const dist = (ax: number, ay: number, bx: number, by: number) =>
  Math.hypot(ax - bx, ay - by)
const START_DIST = dist(START.x, START.y, DOCK.x, DOCK.y)

function EntrustPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0
  const darkMode = state.initialData?.darkMode ?? false

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // device = live position of your drifting device (0-100). armed once it docks.
  const [device, setDevice] = useState(reduce ? DOCK : START)
  const [armed, setArmed] = useState(reduce ? true : false)
  const [dragging, setDragging] = useState(false)
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)
  const [committed, setCommitted] = useState(false)

  const deviceRef = useRef(device)
  const armedRef = useRef(armed)
  const touchedRef = useRef(false)
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const timers = useRef<number[]>([])
  const push = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, ms))

  // Warm the safe-area bars to the field so there are no cold bars.
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setIgnoreFooterInset(true)
    actions.setHeaderInsetColor(darkMode ? "#141024" : "#eef1fb")
    actions.setFooterInsetColor(darkMode ? "#0c0a18" : "#e6eaf7")
  }, [actions, darkMode])

  const setDeviceTo = useCallback((p: { x: number; y: number }) => {
    deviceRef.current = p
    setDevice(p)
    if (!armedRef.current && dist(p.x, p.y, DOCK.x, DOCK.y) <= SNAP_R) {
      armedRef.current = true
      deviceRef.current = DOCK
      setDevice(DOCK)
      setArmed(true)
      native.logMixpanelEvent("html_entrust_paired")
    }
  }, [])

  const toLocal = (clientX: number, clientY: number) => {
    const rect = surfaceRef.current?.getBoundingClientRect()
    if (!rect) return null
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    }
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (armedRef.current) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    touchedRef.current = true
    setDragging(true)
    const loc = toLocal(e.clientX, e.clientY)
    if (loc) setDeviceTo(loc)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || armedRef.current) return
    const loc = toLocal(e.clientX, e.clientY)
    if (loc) setDeviceTo(loc)
  }
  const endDrag = () => {
    setDragging(false)
    // If released short of the dock, ease the device back to where it drifted.
    if (!armedRef.current) {
      const p = deviceRef.current
      if (dist(p.x, p.y, DOCK.x, DOCK.y) > SNAP_R) setDeviceTo(START)
    }
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== " " && e.key !== "Enter") return
    e.preventDefault()
    if (armedRef.current) return
    touchedRef.current = true
    setDeviceTo(DOCK)
  }

  // On load: reveal the close affordance, then auto-pair so passive viewers and
  // screenshots land on the sealed, armed circle. If the user grabs the device
  // first, the auto-pair stands down.
  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1500)
    if (reduce) return () => timers.current.forEach(window.clearTimeout)
    const T = 34 // steps
    const step = (i: number) => {
      if (touchedRef.current || armedRef.current) return
      const t = i / T
      // ease-in-out along a slight inward arc from START to DOCK.
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
      const bow = Math.sin(t * Math.PI) * 10 // curve the approach
      setDeviceTo({
        x: START.x + (DOCK.x - START.x) * e - bow,
        y: START.y + (DOCK.y - START.y) * e,
      })
      if (i < T) push(() => step(i + 1), 26)
    }
    push(() => step(1), 1050)
    return () => timers.current.forEach(window.clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    native.logMixpanelEvent("html_entrust_purchase", { plan })
    push(() => {
      if (product) native.subscribe(product)
    }, 700)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  // Tether geometry: core → device, in 0-100 space with a gentle bow.
  const midX = (CORE.x + device.x) / 2 + (armed ? 0 : 6)
  const midY = (CORE.y + device.y) / 2 - 6
  const tetherD = `M${CORE.x} ${CORE.y} Q${midX} ${midY} ${device.x} ${device.y}`
  const linkLen = dist(CORE.x, CORE.y, device.x, device.y)

  const progress = armed
    ? 1
    : Math.max(
        0,
        Math.min(1, 1 - dist(device.x, device.y, DOCK.x, DOCK.y) / START_DIST),
      )

  const readout = armed
    ? "Paired · your circle is closed, full watch on"
    : dragging || touchedRef.current
      ? `Bringing your device home — ${Math.round(progress * 100)}%`
      : "Private sync is on standby right now"

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        armed ? styles.isArmed : ""
      } ${committed ? styles.isCommitted : ""}`}
      style={{ ["--p" as any]: progress }}
    >
      <div className={styles.field} aria-hidden>
        <span className={styles.aura} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 12, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar — brand clarity */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowDot} aria-hidden />
            Premium sync is paused
          </p>
          <h1 className={styles.title}>
            Bring it all back
            <br />
            <span className={styles.titleThen}>into your circle.</span>
          </h1>
        </header>

        {/* ── The trusted-circle instrument ────────────────────────────────── */}
        <section className={styles.stageWrap} aria-label="Re-pair your device">
          <div
            ref={surfaceRef}
            className={`${styles.surface} ${dragging ? styles.surfaceLive : ""}`}
          >
            {/* dormant/armed link between the vault core and your device */}
            <svg
              className={styles.link}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path className={styles.linkGlow} d={tetherD} />
              <path className={styles.linkLine} d={tetherD} />
              {armed &&
                [0, 1, 2].map((i) => (
                  <circle
                    key={i}
                    className={styles.spark}
                    r="1.6"
                    style={{ ["--d" as any]: i }}
                  >
                    <animateMotion
                      dur="1.7s"
                      begin={`${i * 0.55}s`}
                      repeatCount="indefinite"
                      path={tetherD}
                    />
                  </circle>
                ))}
            </svg>

            {/* the trusted ring + four protection nodes */}
            <div className={styles.ring} aria-hidden />
            {GUARDS.map((g) => {
              const p = onRing(g.deg)
              return (
                <span
                  key={g.label}
                  className={`${styles.guard} ${armed ? styles.guardOn : ""}`}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  <span className={styles.guardOrb}>{g.icon}</span>
                  <span className={styles.guardLabel}>{g.label}</span>
                </span>
              )
            })}

            {/* the vault core holding your memories */}
            <div
              className={styles.core}
              style={{ left: `${CORE.x}%`, top: `${CORE.y}%` }}
            >
              <span className={styles.coreRing} aria-hidden />
              <div className={styles.coreStack} aria-hidden>
                {CORE_MEMORIES.map((src, i) => (
                  <span
                    key={src}
                    className={styles.tile}
                    style={{ ["--i" as any]: i - 1 }}
                  >
                    <img src={src} alt="" draggable={false} loading="eager" />
                  </span>
                ))}
                <span className={styles.coreLock} aria-hidden>
                  <svg viewBox="0 0 24 24" width="13" height="13">
                    <path
                      d="M8 10V7.4a4 4 0 0 1 8 0V10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <rect
                      x="5.5"
                      y="10"
                      width="13"
                      height="9.4"
                      rx="2.4"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </div>
            </div>

            {/* your device — drag it home to its dock */}
            <button
              className={`${styles.device} ${armed ? styles.deviceHome : ""} ${
                dragging ? styles.deviceGrab : ""
              }`}
              style={{ left: `${device.x}%`, top: `${device.y}%` }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={onKeyDown}
              aria-label="Drag your device back into your trusted circle to re-pair"
            >
              <span className={styles.phone} aria-hidden>
                <span className={styles.phoneGrid}>
                  {MEMORIES.slice(0, 4).map((src) => (
                    <span key={src} className={styles.phoneCell}>
                      <img src={src} alt="" draggable={false} loading="eager" />
                    </span>
                  ))}
                </span>
                <span className={styles.phoneNotch} />
              </span>
              {!armed && !touchedRef.current && (
                <span className={styles.hint}>
                  <span className={styles.hintPulse} aria-hidden />
                  Drag home
                </span>
              )}
            </button>

            {/* handshake ripple on pairing */}
            {armed && <span className={styles.ripple} aria-hidden />}
          </div>

          {/* live readout */}
          <div className={styles.readout} aria-live="polite">
            {armed ? (
              <span className={styles.readOn}>
                <span className={styles.readDot} />
                {readout}
              </span>
            ) : (
              <span className={styles.readOff}>{readout}</span>
            )}
          </div>
        </section>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Every device, all year</span>
                <span className={styles.planCovers}>
                  7-day free trial, then billed yearly
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed
                ? "Closing your circle…"
                : "Reconnect Premium — 7 days free"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos never left this phone — they're yours, always. Premium
            just brings your private, end-to-end encrypted sync and full watch
            back across every device. Nothing is ever deleted or locked away
            from you.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EntrustPaywall
